import React, { useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';

const PRIMARY_BLUE = '#0B3187';
const HEADER_BG = '#f0f4fb';

const formatDate = (value) => {
    if (!value) return '-';
    try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        return d.toLocaleDateString('tr-TR');
    } catch {
        return value;
    }
};

const ItemReplacementDetailsDialog = ({ visible, onHide, data, isLoading, itemCode, chassisNo }) => {
    const { th } = useLanguage();
    const navigate = useNavigate();

    const items = useMemo(() => {
        if (!data) return [];
        const raw = data?.data?.items ?? data?.items ?? [];
        return Array.isArray(raw) ? raw : [];
    }, [data]);

    const dialogHeader = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <span
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '2rem',
                    height: '2rem',
                    borderRadius: '50%',
                    background: PRIMARY_BLUE,
                    flexShrink: 0
                }}
            >
                <i className="pi pi-sync" style={{ color: '#fff', fontSize: '0.9rem' }} />
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                <span style={{ fontSize: '1rem', fontWeight: 700, color: PRIMARY_BLUE, lineHeight: 1.2 }}>{th('warrantyClaims.detail.partTable.replacementDetailsTitle')}</span>
                {(itemCode || chassisNo) && (
                    <span style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.2 }}>
                        {itemCode && (
                            <>
                                {th('warrantyClaims.detail.partTable.replacementDetailsItemCode')}: <strong style={{ color: PRIMARY_BLUE }}>{itemCode}</strong>
                            </>
                        )}
                        {itemCode && chassisNo && <span style={{ margin: '0 0.4rem', color: '#cbd5e1' }}>·</span>}
                        {chassisNo && (
                            <>
                                {th('warrantyClaims.detail.partTable.replacementDetailsChassisNo')}: <strong style={{ color: PRIMARY_BLUE }}>{chassisNo}</strong>
                            </>
                        )}
                    </span>
                )}
            </div>
        </div>
    );

    const claimNoBody = (row) => {
        const claimNo = row?.warrClaimsNo;
        const claimId = row?.claimId;

        if (!claimNo) return '-';

        const handleNavigate = () => {
            onHide();
            navigate(`/warranty-management/warranty-requests/claims/${claimId}`);
        };

        if (!claimId) {
            return <span style={{ fontWeight: 600 }}>{claimNo}</span>;
        }

        return (
            <span
                role="button"
                tabIndex={0}
                onClick={handleNavigate}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNavigate();
                    }
                }}
                style={{
                    color: PRIMARY_BLUE,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 600
                }}
            >
                {claimNo}
            </span>
        );
    };

    return (
        <Dialog
            visible={visible}
            header={dialogHeader}
            style={{ width: '80vw', maxWidth: '1100px' }}
            onHide={onHide}
            modal
            closable
            draggable={false}
            resizable={false}
            contentStyle={{ padding: '1.25rem' }}
            footer={
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button label={th('common.close')} icon="pi pi-times" onClick={onHide} style={{ background: PRIMARY_BLUE, border: 'none', borderRadius: '0.4rem' }} />
                </div>
            }
        >
            {isLoading ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '160px'
                    }}
                >
                    <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="4" />
                </div>
            ) : items.length === 0 ? (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.75rem',
                        minHeight: '140px',
                        color: '#64748b'
                    }}
                >
                    <i className="pi pi-inbox" style={{ fontSize: '2.5rem', color: '#94a3b8' }} />
                    <span style={{ fontSize: '0.95rem' }}>{th('warrantyClaims.detail.partTable.replacementDetailsNotFound')}</span>
                </div>
            ) : (
                <DataTable
                    value={items}
                    showGridlines
                    stripedRows
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    emptyMessage={th('warrantyClaims.detail.partTable.replacementDetailsNotFound')}
                    tableStyle={{ minWidth: '50rem' }}
                    pt={{
                        thead: { style: { background: HEADER_BG } }
                    }}
                >
                    <Column
                        field="itemCode"
                        header={th('warrantyClaims.detail.partTable.replacementDetailsItemCode')}
                        sortable
                        style={{ minWidth: '120px' }}
                        body={(row) => <span style={{ fontWeight: 600, color: PRIMARY_BLUE }}>{row?.itemCode || '-'}</span>}
                    />
                    <Column
                        field="chassisNo"
                        header={th('warrantyClaims.detail.partTable.replacementDetailsChassisNo')}
                        sortable
                        style={{ minWidth: '180px' }}
                        body={(row) => <span style={{ fontFamily: 'monospace', fontSize: '0.88rem', color: '#334155' }}>{row?.chassisNo || '-'}</span>}
                    />
                    <Column field="warrClaimsNo" header={th('warrantyClaims.detail.partTable.replacementDetailsClaimNo')} sortable style={{ minWidth: '150px' }} body={claimNoBody} />
                    <Column field="dealer" header={th('warrantyClaims.detail.partTable.replacementDetailsDealer')} sortable style={{ minWidth: '200px' }} body={(row) => <span>{row?.dealer || '-'}</span>} />
                    <Column field="createdBy" header={th('warrantyClaims.detail.partTable.replacementDetailsCreatedBy')} sortable style={{ minWidth: '200px' }} body={(row) => <span>{row?.createdBy || '-'}</span>} />
                    <Column field="createdDate" header={th('warrantyClaims.detail.partTable.replacementDetailsCreatedDate')} sortable style={{ minWidth: '140px' }} body={(row) => <span>{formatDate(row?.createdDate)}</span>} />
                </DataTable>
            )}
        </Dialog>
    );
};

export default ItemReplacementDetailsDialog;
